// client/src/pages/secure/TransactionDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DocumentManager from '../../components/secure/DocumentManager';
import ActivityTimeline from '../../components/secure/ActivityTimeline';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import ReferralFees from '../../components/secure/ReferralFees';
import DealMilestonesPanel from '../../components/secure/DealMilestonesPanel';
import ContractReaderPanel from '../../components/secure/ContractReaderPanel';
import TcChecklistPanel from '../../components/secure/TcChecklistPanel';

const dateOnly = (v) => v ? new Date(v).toISOString().split('T')[0] : '';

const MILESTONE_FIELDS = [
    { key: 'attorney_review_deadline', label: 'Attorney Review Deadline' },
    { key: 'inspection_deadline', label: 'Inspection Deadline' },
    { key: 'financing_contingency_deadline', label: 'Financing Contingency Deadline' },
    { key: 'appraisal_deadline', label: 'Appraisal Deadline' },
    { key: 'earnest_money_due_date', label: 'Earnest Money Due' },
];

// --- Edit Transaction Modal ---
const EditTransactionModal = ({ isOpen, onClose, transaction, onTransactionUpdated }) => {
    const [formData, setFormData] = useState(null);
    const [allClients, setAllClients] = useState([]);
    const [allAgents, setAllAgents] = useState([]);
    const apiClient = useApiClient();

    useEffect(() => {
        if (isOpen && transaction) {
            setFormData({
                status: transaction.status,
                final_price: transaction.final_price || '',
                actual_close_date: dateOnly(transaction.actual_close_date),
                contract_acceptance_date: dateOnly(transaction.contract_acceptance_date),
                attorney_review_deadline: dateOnly(transaction.attorney_review_deadline),
                inspection_deadline: dateOnly(transaction.inspection_deadline),
                financing_contingency_deadline: dateOnly(transaction.financing_contingency_deadline),
                appraisal_deadline: dateOnly(transaction.appraisal_deadline),
                earnest_money_due_date: dateOnly(transaction.earnest_money_due_date),
                participants: {
                    clients: transaction.participants.clients.map(c => ({...c})),
                    agents: transaction.participants.agents.map(a => ({...a}))
                }
            });

            Promise.all([
                apiClient.get('/api/clients'),
                apiClient.get('/api/agents')
            ]).then(([clientData, agentData]) => {
                setAllClients(clientData);
                setAllAgents(agentData);
            });
        }
    }, [isOpen, transaction, apiClient]);
    
    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    
    const handleParticipantChange = (type, index, value) => {
        const newParticipants = { ...formData.participants };
        if (type === 'agent') {
            newParticipants.agents[index].agent_id = value;
        } else {
            newParticipants.clients[index].client_id = value;
        }
        setFormData(prev => ({...prev, participants: newParticipants }));
    };

    const handleAgentSplitChange = (index, value) => {
        const newParticipants = { ...formData.participants };
        newParticipants.agents[index].commission_split_percentage = value;
        setFormData(prev => ({ ...prev, participants: newParticipants }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const r = await apiClient.put(`/api/transactions/${transaction.transaction_id}`, formData);
            onTransactionUpdated(r?.automated);
            onClose();
        } catch (error) {
            console.error("Failed to update transaction:", error);
        }
    };
    
    if (!isOpen || !formData) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-4 sm:p-8 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto w-full max-w-2xl">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Edit Transaction</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Status</label>
                        <select name="status" value={formData.status} onChange={handleChange} className="mt-1 block w-full p-2 border rounded-md"><option>Active Listing</option><option>Pending</option><option>Closed</option><option>Cancelled</option></select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Final Price</label>
                        <input name="final_price" type="number" step="0.01" value={formData.final_price} onChange={handleChange} className="mt-1 block w-full p-2 border rounded-md" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Actual Close Date</label>
                        <input name="actual_close_date" type="date" value={formData.actual_close_date} onChange={handleChange} className="mt-1 block w-full p-2 border rounded-md" />
                    </div>

                    <div className="border-t pt-4 mt-4">
                        <h3 className="text-lg font-semibold text-slate-800 mb-2">Milestones</h3>
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-slate-700">Contract acceptance date</label>
                            <input name="contract_acceptance_date" type="date" value={formData.contract_acceptance_date || ''} onChange={handleChange} className="mt-1 block w-full md:w-1/2 p-2 border rounded-md" />
                            <p className="text-xs text-slate-500 mt-1">Leave a deadline blank and the office fills the ones the standard contract fixes (Illinois 8.0: attorney review and inspection, 5 business days). Enter the mortgage and earnest-money dates from the executed contract.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {MILESTONE_FIELDS.map(({ key, label }) => (
                                <div key={key}>
                                    <label className="block text-sm font-medium text-slate-700">{label}</label>
                                    <input name={key} type="date" value={formData[key] || ''} onChange={handleChange} className="mt-1 block w-full p-2 border rounded-md" />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="border-t pt-4 mt-4">
                        <h3 className="text-lg font-semibold text-slate-800">Participants</h3>
                        {formData.participants.clients.map((client, index) => (
                            <div key={index} className="mt-2"><label className="block text-sm font-medium">{client.client_role}</label><select value={client.client_id} onChange={(e) => handleParticipantChange('client', index, e.target.value)} className="w-full p-2 border rounded">{allClients.map(c => <option key={c.client_id} value={c.client_id}>{c.first_name} {c.last_name}</option>)}</select></div>
                        ))}
                         {formData.participants.agents.map((agent, index) => (
                            <div key={index} className="mt-2 flex gap-2 items-end">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium">Agent</label>
                                    <select value={agent.agent_id} onChange={(e) => handleParticipantChange('agent', index, e.target.value)} className="w-full p-2 border rounded">{allAgents.map(a => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}</select>
                                </div>
                                <div className="w-32">
                                    <label className="block text-sm font-medium">Split %</label>
                                    <input type="number" step="0.01" value={agent.commission_split_percentage || ''} onChange={(e) => handleAgentSplitChange(index, e.target.value)} className="w-full p-2 border rounded" />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-end gap-4 pt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-slate-800 text-white rounded-md">Save Changes</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Main Detail Page Component ---
const TransactionDetailPage = () => {
    const { id } = useParams();
    const [transaction, setTransaction] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isGeneratingStatement, setIsGeneratingStatement] = useState(false);
    const [notice, setNotice] = useState('');
    const apiClient = useApiClient();
    const currentUser = getCurrentUser();
    const [milestonesKey, setMilestonesKey] = useState(0);
    const [tcKey, setTcKey] = useState(0); // bumps when milestones move so the TC checklist reloads its dates
    const canViewFinancials = !!(currentUser?.isManagingBroker || currentUser?.staffRole === 'Bookkeeper');

    const handleDownloadCommissionStatement = async () => {
        setIsGeneratingStatement(true);
        try {
            const response = await apiClient.post(`/api/forms/generate-commission-statement/${id}`, {}, { responseType: 'blob' });
            const file = new Blob([response.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = `CommissionStatement_Transaction${id}.pdf`;
            link.click();
            URL.revokeObjectURL(fileURL);
        } catch (err) {
            alert(err.message || 'Failed to generate commission statement.');
        } finally {
            setIsGeneratingStatement(false);
        }
    };

    const fetchTransactionDetails = useCallback(async () => {
        // Don't set loading to true on refetch, only on initial load
        if (!transaction) setIsLoading(true);
        try {
            const data = await apiClient.get(`/api/transactions/${id}`);
            setTransaction(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch transaction details.');
        } finally {
            setIsLoading(false);
        }
    }, [id, apiClient, transaction]);

    useEffect(() => {
        fetchTransactionDetails();
    }, [fetchTransactionDetails]); // --- This line is now corrected ---

    // What the automation did on save, in words.
    const afterSave = (automated) => {
        const bits = [];
        const set = automated?.deadlines_set || {};
        if (Object.keys(set).length) bits.push(`Deadlines filled from the acceptance date: ${Object.entries(set).map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`).join('; ')}.`);
        if (automated?.ask_for?.length) bits.push(`Still needed from the contract: ${automated.ask_for.join(', ')}.`);
        if (automated?.pipeline?.length) bits.push(`Client pipeline moved to ${automated.pipeline.join(', ')}.`);
        setNotice(bits.join(' '));
        fetchTransactionDetails();
    };

    const submitForReview = async () => {
        const note = window.prompt('Anything the managing broker should know about this file? (optional)') ?? null;
        if (note === null) return;
        try { const r = await apiClient.post(`/api/transactions/${id}/submit-review`, { note }); setNotice(r.message); fetchTransactionDetails(); }
        catch (err) { setNotice(err.message); }
    };
    const review = async (decision) => {
        const note = decision === 'returned' ? window.prompt('What needs fixing?') : (window.prompt('Note (optional)') ?? '');
        if (decision === 'returned' && !note) return;
        try { const r = await apiClient.post(`/api/transactions/${id}/review`, { decision, note }); setNotice(r.message); fetchTransactionDetails(); }
        catch (err) { setNotice(err.message); }
    };

    if (isLoading) return <div className="p-8">Loading transaction...</div>;
    if (error) return <div className="p-8 text-red-500">{error}</div>;
    if (!transaction) return null;

    return (
        <>
            <EditTransactionModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} transaction={transaction} onTransactionUpdated={afterSave} />
            <div className="space-y-8">
                <div><Link to="/dashboard/transactions" className="text-indigo-600 hover:text-indigo-900">&larr; Back to Transactions</Link></div>
                <div className="bg-white p-4 sm:p-8 rounded-lg shadow-md">
                     <div className="flex flex-wrap gap-3 justify-between items-center">
                        <h1 className="text-3xl font-bold text-slate-800">{transaction.street_address}</h1>
                        <div className="flex gap-3">
                            {canViewFinancials && transaction.status === 'Closed' && (
                                <button onClick={handleDownloadCommissionStatement} disabled={isGeneratingStatement} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-green-400">
                                    {isGeneratingStatement ? 'Generating...' : 'Commission Statement'}
                                </button>
                            )}
                            <button onClick={() => setIsEditModalOpen(true)} className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900">Edit Transaction</button>
                        </div>
                    </div>
                    <p className="mt-2 text-lg font-semibold text-gray-600">{transaction.status}
                        {transaction.contract_acceptance_date && <span className="ml-3 text-sm font-normal text-slate-500">accepted {new Date(transaction.contract_acceptance_date).toLocaleDateString()}</span>}
                    </p>
                    {notice && <div className="mt-3 p-3 rounded bg-indigo-50 text-indigo-900 text-sm">{notice}</div>}

                    {['Closed', 'Cancelled'].includes(transaction.status) && (
                        <div className="mt-4 border-t pt-4 flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <p className="text-sm font-medium text-slate-500">File review</p>
                                <p className={`text-lg ${transaction.file_review_status === 'approved' ? 'text-emerald-700' : transaction.file_review_status === 'returned' ? 'text-red-700' : 'text-slate-900'}`}>
                                    {{ not_started: 'Not yet submitted', submitted: 'Waiting for the managing broker', approved: 'Approved', returned: 'Returned to the agent' }[transaction.file_review_status] || '—'}
                                </p>
                                {transaction.file_review_note && <p className="text-sm text-slate-600 italic">{transaction.file_review_note}</p>}
                            </div>
                            <div className="flex gap-2">
                                {['not_started', 'returned'].includes(transaction.file_review_status) &&
                                    <button onClick={submitForReview} className="px-3 py-2 text-sm bg-slate-800 text-white rounded">Submit for review</button>}
                                {currentUser?.isManagingBroker && transaction.file_review_status === 'submitted' && (<>
                                    <button onClick={() => review('approved')} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded">Approve file</button>
                                    <button onClick={() => review('returned')} className="px-3 py-2 text-sm bg-slate-200 rounded">Return to agent</button>
                                </>)}
                            </div>
                        </div>
                    )}

                    {MILESTONE_FIELDS.some(({ key }) => transaction[key]) && (
                        <div className="mt-4 border-t pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                            {MILESTONE_FIELDS.filter(({ key }) => transaction[key]).map(({ key, label }) => {
                                const dateVal = new Date(transaction[key]);
                                const isOverdue = dateVal < new Date() && !['Closed', 'Cancelled'].includes(transaction.status);
                                return (
                                    <div key={key}>
                                        <p className="text-sm font-medium text-slate-500">{label}</p>
                                        <p className={`text-lg ${isOverdue ? 'text-red-600 font-semibold' : 'text-slate-900'}`}>
                                            {dateVal.toLocaleDateString()}{isOverdue && ' (overdue)'}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {transaction.participants?.agents?.some(a => a.commission_split_percentage) && (
                        <div className="mt-4 border-t pt-4">
                            <p className="text-sm font-medium text-slate-500 mb-1">Commission Split</p>
                            {transaction.participants.agents.map(a => (
                                <p key={a.agent_id} className="text-slate-900">{a.name}: {a.commission_split_percentage ? `${a.commission_split_percentage}%` : 'N/A'}</p>
                            ))}
                        </div>
                    )}
                </div>

                <ContractReaderPanel transactionId={id} onApplied={() => { setMilestonesKey((k) => k + 1); setTcKey((k) => k + 1); }} />
                <DealMilestonesPanel key={milestonesKey} transactionId={id} onChanged={() => setTcKey((k) => k + 1)} />
                <TcChecklistPanel transactionId={id} refresh={tcKey} />

                <div>
                    <DocumentManager
                        relatedId={id}
                        relatedType="transaction"
                        documentTypes={['Purchase Agreement', 'Disclosure', 'Inspection Report', 'Appraisal', 'Title Commitment', 'Closing Statement', 'Other']}
                    />
                </div>

                {canViewFinancials && <ReferralFees transactionId={id} />}

                <ActivityTimeline transactionId={id} />
            </div>
        </>
    );
};

export default TransactionDetailPage;